import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Key, Trash2, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";

const PROVIDERS = [
  { id: "anthropic", label: "Anthropic (Claude)", placeholder: "sk-ant-…",
    help: "console.anthropic.com → API Keys" },
  { id: "openai", label: "OpenAI (GPT)", placeholder: "sk-…",
    help: "platform.openai.com → API keys" },
  { id: "gemini", label: "Google (Gemini)", placeholder: "AIza…",
    help: "aistudio.google.com → API key" },
];

export default function LlmSettings() {
  const [state, setState] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = () => api.get("/settings/llm").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);

  const save = async (provider) => {
    const val = (drafts[provider] || "").trim();
    if (!val) return toast.error("Pega la clave primero");
    try {
      await api.put("/settings/llm", { provider, api_key: val });
      toast.success("Clave guardada (cifrada)");
      setDrafts((d) => ({ ...d, [provider]: "" }));
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fallo al guardar"); }
  };

  const remove = async (provider) => {
    if (!window.confirm(`¿Eliminar la clave de ${provider}?`)) return;
    await api.delete(`/settings/llm/${provider}`);
    toast.success("Clave eliminada");
    load();
  };

  const isMax = state?.mode === "maxplan";

  return (
    <div className="p-8 space-y-6" data-testid="llm-settings-page">
      <div className="border-b border-[var(--border)] pb-5">
        <div className="label-mono mb-2">CONFIGURACIÓN · MODELOS</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Claves LLM</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          Cómo se resuelven las respuestas de tus agentes de IA.
        </p>
      </div>

      {!state && (
        <div className="p-6 text-sm text-[var(--text-dim)] font-mono">Cargando…</div>
      )}

      {isMax && (
        <div className="border border-[var(--accent)] bg-[var(--accent)]/5 p-5" data-testid="llm-maxplan">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-[var(--accent)]" />
            <div className="label-mono" style={{ color: "var(--accent)" }}>CUENTA DEL DUEÑO · PLAN MAX</div>
          </div>
          <p className="text-sm text-white">
            Esta cuenta resuelve sus agentes con <strong>Claude</strong> en el servidor
            (tu plan Max). No necesitas configurar ninguna clave de API ni pagas por token.
          </p>
        </div>
      )}

      {state && !isMax && (
        <>
          <div className="border border-[var(--border)] bg-[var(--surface)] p-4 flex items-start gap-3">
            <ShieldCheck className="w-4 h-4 text-[var(--accent)] mt-0.5" />
            <p className="text-xs text-[var(--text-dim)]">
              Tus claves se guardan <strong>cifradas</strong> y solo se usan para tus
              propios agentes. Nunca se muestran enteras ni salen de este servidor.
              Cada agente usa el proveedor con el que está configurado; añade la clave
              del proveedor que necesites.
            </p>
          </div>

          {PROVIDERS.map((p) => {
            const info = state.providers[p.id] || {};
            return (
              <div key={p.id} className="border border-[var(--border)] bg-[var(--surface)] p-5"
                   data-testid={`llm-${p.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Key className={`w-4 h-4 ${info.configured ? "text-[var(--accent)]" : "text-[var(--text-dim)]"}`} />
                    <span className="text-sm font-semibold text-white">{p.label}</span>
                    {info.configured ? (
                      <Badge className="rounded-none font-mono text-[9px] bg-[var(--accent)]/10 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10">
                        {info.mask}
                      </Badge>
                    ) : (
                      <Badge className="rounded-none font-mono text-[9px] bg-[var(--surface-2)] border border-[var(--border)] text-[var(--text-dim)] hover:bg-[var(--surface-2)]">
                        SIN CONFIGURAR
                      </Badge>
                    )}
                  </div>
                  {info.configured && (
                    <button onClick={() => remove(p.id)}
                            className="text-[var(--text-dim)] hover:text-[var(--danger)]"
                            data-testid={`llm-remove-${p.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <Label className="label-mono">{info.configured ? "Reemplazar clave" : "Clave de API"}</Label>
                <div className="flex items-center gap-2 mt-2">
                  <Input
                    type="password"
                    value={drafts[p.id] || ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                    placeholder={p.placeholder}
                    className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono"
                    data-testid={`llm-input-${p.id}`}
                  />
                  <Button onClick={() => save(p.id)}
                          className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80"
                          data-testid={`llm-save-${p.id}`}>
                    Guardar
                  </Button>
                </div>
                <div className="text-[10px] text-[var(--text-dim)] font-mono mt-2">{p.help}</div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
